import React, { lazy, Suspense } from 'react'; // 1. Importam lazy si Suspense
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';
import { ToastContainer } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import { Flowbite } from 'flowbite-react'; // Putem folosi Spinner din Flowbite daca exista, sau HTML simplu

// --- Importuri Statice (Layout & Core) ---
// Acestea raman statice pentru ca utilizatorul sa vada meniul imediat
import MainNavBar from './components/MainNavBar';
import MobileMenu from './components/MobileMenu';
import EnvironmentBanner from './components/EnvironmentBanner';
import ContactButtons from './components/ContactButtons';
import AssistantPanel from './components/Assistant/AssistantPanel';
import AssistantEntityHost from './components/Assistant/AssistantEntityHost';
import VersionMismatchPopup from './components/VersionMismatchPopup';
import ChangelogModal from './components/ChangelogModal';
import client from './apollo-client';
import customTheme from './functions/customTheme';
import { UserProvider, useUser } from './components/UserContext';
import { MenuProvider } from './components/MenuContext';
import { VersionProvider } from './contexts/VersionContext';
import { DateRangeProvider } from './contexts/DateRangeContext';
import { UserPreferencesProvider } from './contexts/UserPreferencesContext';
import { ThemeProvider } from './contexts/ThemeContext';

// --- Importuri Lazy (Pagini) ---
// Acum fiecare pagina este un fisier JS separat, incarcat doar la nevoie
const Clients = lazy(() => import('./pages/clients/Clients'));
const Users = lazy(() => import('./pages/users/Users'));
const Accounts = lazy(() => import('./pages/accounts/Accounts'));
const Roles = lazy(() => import('./pages/roles/Roles'));
const Offers = lazy(() => import('./pages/offers/Offers'));
const Login = lazy(() => import('./pages/auth/Login'));
const Unauthorized = lazy(() => import('./pages/auth/Unauthorized'));
const NotFoundPage = lazy(() => import('./pages/auth/NotFoundPage'));
const ProfileSettings = lazy(() => import('./pages/auth/ProfileSettings'));
const Recommendations = lazy(() => import('./pages/recommendations/Recommendations'));
const RecommandationsRedirect = lazy(() => import('./pages/recommandations/Recommandations'));
const Bookings = lazy(() => import('./pages/bookings/Bookings'));
const Movements = lazy(() => import('./pages/movements/Movements'));
const MovementsFile = lazy(() => import('./pages/movements/file/MovementsFile'));
const Emails = lazy(() => import('./pages/bookings/emails/Emails'));
const Manifests = lazy(() => import('./pages/bookings/manifests/Manifests'));
const Freights = lazy(() => import('./pages/bookings/freights/Freights'));
const YmlRoutes = lazy(() => import('./pages/ymlRoutes/YmlRoutes'));
const Bills = lazy(() => import('./pages/bookings/bills/Bills'));
const Containers = lazy(() => import('./pages/containers/Containers'));
const Yard = lazy(() => import('./pages/yard/Yard'));
const NoticeSettings = lazy(() => import('./pages/settings/NoticeSettings'));
const ContainerSettings = lazy(() => import('./pages/settings/containers/ContainerSettings'));
const VesselEstimationSettings = lazy(() => import('./pages/settings/VesselEstimationSettings'));
const ImportOfferVesselSettings = lazy(() => import('./pages/settings/ImportOfferVesselSettings'));
const YmlP2pSettings = lazy(() => import('./pages/settings/ymlP2p/YmlP2pSettings'));
const YmlP2pRoutes = lazy(() => import('./pages/settings/ymlP2p/YmlP2pRoutes'));
const AiAssistantSettings = lazy(() => import('./pages/settings/AiAssistantSettings'));
const PortalContactSettings = lazy(() => import('./pages/settings/PortalContactSettings'));
const PresentationSettings = lazy(() => import('./pages/settings/presentation/PresentationSettings'));
const AiUsagePage = lazy(() => import('./pages/settings/aiUsage/AiUsagePage'));
const EnvironmentSettings = lazy(() => import('./pages/settings/EnvironmentSettings'));
const ScrapeDoSettings = lazy(() => import('./pages/settings/ScrapeDoSettings'));
const GeoLocationSettings = lazy(() => import('./pages/settings/GeoLocationSettings'));
const DbfViewer = lazy(() => import('./pages/settings/dbfViewer/DbfViewer'));
const Tools = lazy(() => import('./pages/settings/tools/Tools'));
const PortMirrors = lazy(() => import('./pages/settings/portMirrors/Index'));
const PortsSettings = lazy(() => import('./pages/settings/ports/Ports'));
const InternalTransport = lazy(() => import('./pages/settings/internalTransport/InternalTransport'));
const EmailSyncSettings = lazy(() => import('./pages/settings/emailSync/EmailSyncSettings'));
const Monitoring = lazy(() => import('./pages/monitoring/Index'));
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard'));
const SalesDashboard = lazy(() => import('./pages/salesDashboard/SalesDashboard'));
const Rapoarte = lazy(() => import('./pages/rapoarte/Rapoarte'));
const Calendar = lazy(() => import('./pages/calendar/Calendar'));
const Rates = lazy(() => import('./pages/rates/Rates'));
const Newsletter = lazy(() => import('./pages/newsletter/Newsletter'));
const VesselTracking = lazy(() => import('./pages/vesselTracking/VesselTracking'));
const DatadockedSettings = lazy(() => import('./pages/datadockedSettings/DatadockedSettings'));
const VisitReports = lazy(() => import('./pages/visitReports/VisitReports'));
const Market = lazy(() => import('./pages/market/Market'));
const Meetings = lazy(() => import('./pages/meetings/Meetings'));
const Invoices = lazy(() => import('./pages/invoices/Invoices'));
const InvoiceSettings = lazy(() => import('./pages/settings/invoices/InvoiceSettings'));
const OfferSettings = lazy(() => import('./pages/settings/offers/OfferSettings'));
const Pricing = lazy(() => import('./pages/pricing/Pricing'));
const ContainerTypes = lazy(() => import('./pages/settings/containerTypes/Index'));

// --- Componenta de Loading ---
const LoadingFallback = () => (
  <div className="flex items-center justify-center w-full h-full min-h-[200px]">
    {/* Poți folosi Spinner din flowbite sau un div simplu cu animate-spin */}
    <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-600"></div>
  </div>
);

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = !!localStorage.getItem('token');
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

// Redirecționează utilizatorul către pagina lui default (setată în profil), fallback pe /crm/dashboard.
const DefaultRoute: React.FC = () => {
  const { user } = useUser();
  const route = (user?.defaultRoute || 'crm/dashboard').replace(/^\/+/, '');
  return <Navigate to={`/${route}`} replace />;
};

// Coloană pe toată înălțimea ferestrei: `main` primește spațiul RĂMAS după barele de deasupra,
// în loc de o constantă (`calc(100vh - 120px)`) care nu ținea cont de bannerul „MEDIU DE TEST" —
// prezent doar pe staging/dev — și lăsa pagina cu scroll propriu exact cât bannerul.
// `min-h-screen` (nu `h-screen`): paginile cu conținut mai înalt decât ecranul scrollează ca până acum.
const AppLayout: React.FC<{ children: React.ReactNode, guest?: boolean }> = ({ children, guest = false }) => (
  <div className="flex min-h-screen flex-col">
    <EnvironmentBanner />
    { guest ? (<></>) : (<><MainNavBar /><MobileMenu /></>)}
    <ToastContainer limit={2} />
    <Flowbite theme={{ theme: customTheme }}>
      <main className="flex-1 min-h-0 dark:bg-dm-bg dark:text-dm-text">
        {children}
      </main>
    </Flowbite>
  </div>
);

const App: React.FC = () => {
  return (
    <ThemeProvider>
    <ApolloProvider client={client}>
      <VersionProvider>
        <MenuProvider>
          <Router>
            <Routes>
              {/* Ruta de Login - Lazy Loaded */}
              <Route 
                path="/login" 
                element={
                  <AppLayout guest={true}>
                    <Suspense fallback={<LoadingFallback />}>
                      <Login />
                    </Suspense>
                  </AppLayout>
                } 
              />
              
              <Route
                path="/*"
                element={
                  <PrivateRoute>
                    <UserProvider>
                      <UserPreferencesProvider>
                      <DateRangeProvider>
                      <AppLayout>
                        {/* AICI este magia: Suspense inveleste toate rutele interne */}
                        <Suspense fallback={<LoadingFallback />}>
                          <Routes>
                            <Route path="/crm/dashboard" element={<SalesDashboard inCrm />} />
                            <Route path="/crm/clients/*" element={<Clients />} />
                            <Route path="/crm/visit-reports/*" element={<VisitReports />} />
                            <Route path="/crm/sedinte/*" element={<Meetings />} />
                            <Route path="/settings/voice" element={<Navigate to="/settings/profile" replace />} />
                            <Route path="/crm/calendar/*" element={<Calendar />} />
                            <Route path="/crm/newsletter/*" element={<Newsletter />} />
                            <Route path="/crm/offers/*" element={<Offers />} />
                            <Route path="/hr/users/*" element={<Users />} />
                            <Route path="/hr/roles/*" element={<Roles />} />
                            <Route path="/bookings/manifests/*" element={<Manifests />} />
                            <Route path="/bookings/freights/*" element={<Freights />} />
                            <Route path="/bookings/movements/*" element={<Movements />} />
                            <Route path="/bookings/ymlRoutes/*" element={<YmlRoutes />} />
                            <Route path="/bookings/emails/*" element={<Emails />} />
                            <Route path="/bookings/movementsConfiguration/*" element={<MovementsFile />} />
                            <Route path="/bookings/piata/*" element={<Market />} />
                            <Route path="/bookings/bills/*" element={<Bills />} />
                            <Route path="/bookings/*" element={<Bookings />} />
                            <Route path="/containers/*" element={<Containers />} />
                            <Route path="/terminal/*" element={<Yard />} />
                            <Route path="/rates/*" element={<Rates />} />
                            <Route path="/settings/profile" element={<ProfileSettings />} />
                            <Route path="/settings/accounts/*" element={<Accounts />} />
                            <Route path="/settings/notice/*" element={<NoticeSettings />} />
                            <Route path="/settings/containers/*" element={<ContainerSettings />} />
                            <Route path="/settings/vessel-estimation" element={<VesselEstimationSettings />} />
                            <Route path="/settings/import-offer-vessels" element={<ImportOfferVesselSettings />} />
                            <Route path="/crm/offers/point-to-point" element={<YmlP2pRoutes />} />
                            <Route path="/settings/yml-p2p/routes" element={<YmlP2pRoutes />} />
                            <Route path="/settings/yml-p2p" element={<YmlP2pSettings />} />
                            <Route path="/settings/ai-assistant" element={<AiAssistantSettings />} />
                            <Route path="/settings/client-portal" element={<PortalContactSettings />} />
                            <Route path="/settings/presentation" element={<PresentationSettings />} />
                            <Route path="/settings/ai-usage" element={<AiUsagePage />} />
                            <Route path="/settings/environment" element={<EnvironmentSettings />} />
                            <Route path="/settings/scrape-do" element={<ScrapeDoSettings />} />
                            <Route path="/settings/geolocation" element={<GeoLocationSettings />} />
                            <Route path="/settings/tools/*" element={<Tools />} />
                            <Route path="/settings/dbf" element={<DbfViewer />} />
                            <Route path="/settings/port-mirrors" element={<PortMirrors />} />
                            <Route path="/settings/ports" element={<PortsSettings />} />
                            <Route path="/settings/internal-transport/*" element={<InternalTransport />} />
                            <Route path="/settings/email-sync" element={<EmailSyncSettings />} />
                            <Route path="/monitoring/*" element={<Monitoring />} />
                            <Route path="/settings/recommendations/*" element={<Recommendations />} />
                            <Route path="/settings/recommandations/*" element={<RecommandationsRedirect />} />
                            <Route path="/crm" element={<DefaultRoute />} />
                            <Route path="/hr" element={<Navigate to="/hr/users/index" replace />} />
                            <Route path="/dashboard" element={<Dashboard />} />
                            <Route path="/sales-dashboard" element={<SalesDashboard />} />
                            <Route path="/settings" element={<Navigate to="/settings/profile" replace />} />
                            <Route path="/rapoarte/*" element={<Rapoarte />} />
                            <Route path="/vessel-tracking/*" element={<VesselTracking />} />
                            <Route path="/settings/datadocked/*" element={<DatadockedSettings />} />
                            <Route path="/facturi/*" element={<Invoices />} />
                            <Route path="/settings/invoices/*" element={<InvoiceSettings />} />
                            <Route path="/settings/offers" element={<OfferSettings />} />
                            <Route path="/preturi/*" element={<Pricing />} />
                            <Route path="/settings/container-types" element={<ContainerTypes />} />
                            <Route path="/unauthorized" element={<Unauthorized />} />
                            {/* Redirect root către pagina default a utilizatorului */}
                            <Route path="/" element={<DefaultRoute />} />
                            <Route path="*" element={<NotFoundPage />} />
                          </Routes>
                        </Suspense>
                        <AssistantPanel />
                        <AssistantEntityHost />
                      </AppLayout>
                      </DateRangeProvider>
                      </UserPreferencesProvider>
                    </UserProvider>
                  </PrivateRoute>
                }
              />
            </Routes>
          </Router>
          <ContactButtons />
          <VersionMismatchPopup />
          <ChangelogModal />
        </MenuProvider>
      </VersionProvider>
    </ApolloProvider>
    </ThemeProvider>
  );
};

export default App;